import React from 'react';
import { motion } from 'framer-motion';
import { useSite } from '../contexts/SiteContext';
import SafeIcon from '../common/SafeIcon';
import EditableContent from '../components/EditableContent';
import ShareButton from '../components/ShareButton';
import * as FiIcons from 'react-icons/fi';

const { FiCheckCircle, FiShield, FiUsers, FiClipboard, FiFlag, FiAward } = FiIcons;

const TeamPolicyPage = () => {
  const { siteConfig } = useSite();
  
  const policyCategories = [
    { 
      icon: FiShield, 
      title: "Κανόνες Συμπεριφοράς",
      description: "Οι βασικοί κανόνες συμπεριφοράς για όλα τα μέλη της ομάδας"
    },
    { 
      icon: FiUsers, 
      title: "Συμμετοχή & Παρουσία",
      description: "Πολιτικές σχετικά με την παρουσία στις προπονήσεις και τους αγώνες"
    },
    { 
      icon: FiAward, 
      title: "Αθλητικό Πνεύμα",
      description: "Προώθηση του ευ αγωνίζεσθαι και του αθλητικού πνεύματος"
    }
  ];
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <section className="bg-primary-700 text-white py-16 relative" style={{backgroundColor: siteConfig.primary_color}}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              <EditableContent
                page="policy"
                section="header"
                contentKey="title"
                defaultValue="Όροι Ομάδας"
                tag="span"
              />
            </h1>
            <p className="text-xl text-primary-200">
              <EditableContent
                page="policy"
                section="header"
                contentKey="subtitle"
                defaultValue="Κανονισμοί και πολιτικές για όλα τα μέλη της ομάδας μας"
                tag="span"
              />
            </p>
          </motion.div>
        </div>
        <div className="absolute top-4 right-4">
          <ShareButton
            title="Όροι Ομάδας - Α.Ο. Χανιά"
            description="Κανονισμοί και πολιτικές για όλα τα μέλη της ομάδας μας"
            compact={true}
            variant="outline"
            className="bg-white/20 backdrop-blur-sm border-white/30 text-white hover:bg-white/30"
          />
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <SafeIcon icon={FiFlag} className="w-8 h-8 text-primary-600" style={{color: siteConfig.primary_color}} />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              <EditableContent
                page="policy"
                section="intro"
                contentKey="title"
                defaultValue="Η Φιλοσοφία μας"
                tag="span"
              />
            </h2>
            <div className="text-lg text-gray-700 prose max-w-none">
              <EditableContent
                page="policy"
                section="intro"
                contentKey="content"
                defaultValue="Στον Α.Ο. Χανιά, πιστεύουμε ότι ο αθλητισμός είναι κάτι περισσότερο από απλή αγωνιστική δραστηριότητα. Είναι ένα σχολείο ζωής που διδάσκει αξίες όπως η ομαδικότητα, η πειθαρχία, ο σεβασμός και η αφοσίωση. Οι κανονισμοί και οι πολιτικές μας έχουν σχεδιαστεί για να δημιουργήσουν ένα ασφαλές, υποστηρικτικό και θετικό περιβάλλον για όλα τα μέλη της ομάδας μας."
                multiline={true}
                tag="p"
              />
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-primary-50 rounded-xl p-6 border-l-4"
            style={{borderColor: siteConfig.primary_color}}
          >
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <SafeIcon icon={FiClipboard} className="w-6 h-6 text-primary-600" style={{color: siteConfig.primary_color}} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  <EditableContent
                    page="policy"
                    section="intro"
                    contentKey="mission_title"
                    defaultValue="Η Αποστολή μας"
                    tag="span"
                  />
                </h3>
                <p className="text-gray-700">
                  <EditableContent
                    page="policy"
                    section="intro"
                    contentKey="mission_content"
                    defaultValue="Να δημιουργήσουμε ένα περιβάλλον όπου κάθε αθλήτρια μπορεί να αναπτυχθεί τόσο αγωνιστικά όσο και προσωπικά, καλλιεργώντας αξίες που θα την συνοδεύουν σε όλη της τη ζωή."
                    tag="span"
                  />
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Policy Categories Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              <EditableContent
                page="policy"
                section="categories"
                contentKey="title"
                defaultValue="Κατηγορίες Κανονισμών"
                tag="span"
              />
            </h2>
            <p className="text-xl text-gray-600">
              <EditableContent
                page="policy"
                section="categories"
                contentKey="subtitle"
                defaultValue="Οι βασικοί πυλώνες της πολιτικής της ομάδας μας"
                tag="span"
              />
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {policyCategories.map((category, index) => (
              <motion.div
                key={category.title}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 * index }}
                className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all duration-200"
              >
                <div className="w-16 h-16 bg-primary-100 rounded-lg flex items-center justify-center mb-6">
                  <SafeIcon icon={category.icon} className="w-8 h-8 text-primary-600" style={{color: siteConfig.primary_color}} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  <EditableContent
                    page="policy"
                    section="categories"
                    contentKey={`category_${index + 1}_title`}
                    defaultValue={category.title}
                    tag="span"
                  />
                </h3>
                <p className="text-gray-600">
                  <EditableContent
                    page="policy"
                    section="categories"
                    contentKey={`category_${index + 1}_description`}
                    defaultValue={category.description}
                    tag="span"
                  />
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Policies Section */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
              <EditableContent
                page="policy"
                section="detailed"
                contentKey="title"
                defaultValue="Αναλυτικοί Κανονισμοί"
                tag="span"
              />
            </h2>
          </motion.div>

          <div className="space-y-12">
            {/* Conduct Rules */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-gray-50 rounded-xl p-8 border-l-4 border-primary-600"
              style={{borderColor: siteConfig.primary_color}}
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <SafeIcon icon={FiShield} className="w-6 h-6 mr-3 text-primary-600" style={{color: siteConfig.primary_color}} />
                <EditableContent
                  page="policy"
                  section="detailed"
                  contentKey="conduct_title"
                  defaultValue="Κανόνες Συμπεριφοράς"
                  tag="span"
                />
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-start">
                  <SafeIcon icon={FiCheckCircle} className="w-5 h-5 mt-0.5 mr-3 text-primary-600 flex-shrink-0" style={{color: siteConfig.primary_color}} />
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900">
                      <EditableContent
                        page="policy"
                        section="conduct"
                        contentKey="rule_1_title"
                        defaultValue="Σεβασμός"
                        tag="span"
                      />
                    </h4>
                    <p className="text-gray-700">
                      <EditableContent
                        page="policy"
                        section="conduct"
                        contentKey="rule_1_content"
                        defaultValue="Όλα τα μέλη της ομάδας οφείλουν να συμπεριφέρονται με σεβασμό προς τους συμπαίκτες, προπονητές, αντιπάλους, διαιτητές και φιλάθλους. Δεν ανεχόμαστε καμία μορφή λεκτικής ή σωματικής βίας."
                        tag="span"
                      />
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <SafeIcon icon={FiCheckCircle} className="w-5 h-5 mt-0.5 mr-3 text-primary-600 flex-shrink-0" style={{color: siteConfig.primary_color}} />
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900">
                      <EditableContent
                        page="policy"
                        section="conduct"
                        contentKey="rule_2_title"
                        defaultValue="Ομαδικό Πνεύμα"
                        tag="span"
                      />
                    </h4>
                    <p className="text-gray-700">
                      <EditableContent
                        page="policy"
                        section="conduct"
                        contentKey="rule_2_content"
                        defaultValue="Η επιτυχία της ομάδας προέχει των ατομικών επιτυχιών. Οι παίκτριες πρέπει να λειτουργούν ως μία μονάδα, υποστηρίζοντας και ενθαρρύνοντας η μία την άλλη."
                        tag="span"
                      />
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <SafeIcon icon={FiCheckCircle} className="w-5 h-5 mt-0.5 mr-3 text-primary-600 flex-shrink-0" style={{color: siteConfig.primary_color}} />
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900">
                      <EditableContent
                        page="policy"
                        section="conduct"
                        contentKey="rule_3_title"
                        defaultValue="Αντιπροσώπευση"
                        tag="span"
                      />
                    </h4>
                    <p className="text-gray-700">
                      <EditableContent
                        page="policy"
                        section="conduct"
                        contentKey="rule_3_content"
                        defaultValue="Κάθε μέλος της ομάδας αντιπροσωπεύει τον Α.Ο. Χανιά τόσο εντός όσο και εκτός γηπέδου. Η συμπεριφορά στα social media και στις δημόσιες εμφανίσεις πρέπει να είναι σύμφωνη με τις αξίες του συλλόγου."
                        tag="span"
                      />
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
            
            {/* Participation & Attendance */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-gray-50 rounded-xl p-8 border-l-4 border-primary-600"
              style={{borderColor: siteConfig.primary_color}}
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <SafeIcon icon={FiUsers} className="w-6 h-6 mr-3 text-primary-600" style={{color: siteConfig.primary_color}} />
                <EditableContent
                  page="policy"
                  section="detailed"
                  contentKey="attendance_title"
                  defaultValue="Συμμετοχή & Παρουσία"
                  tag="span"
                />
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-start">
                  <SafeIcon icon={FiCheckCircle} className="w-5 h-5 mt-0.5 mr-3 text-primary-600 flex-shrink-0" style={{color: siteConfig.primary_color}} />
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900">
                      <EditableContent
                        page="policy"
                        section="attendance"
                        contentKey="rule_1_title"
                        defaultValue="Προπονήσεις"
                        tag="span"
                      />
                    </h4>
                    <p className="text-gray-700">
                      <EditableContent
                        page="policy"
                        section="attendance"
                        contentKey="rule_1_content"
                        defaultValue="Η τακτική παρουσία στις προπονήσεις είναι υποχρεωτική. Σε περίπτωση αδυναμίας παρουσίας, πρέπει να ενημερώνεται ο προπονητής τουλάχιστον 24 ώρες νωρίτερα, εκτός εξαιρετικών περιπτώσεων."
                        tag="span"
                      />
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <SafeIcon icon={FiCheckCircle} className="w-5 h-5 mt-0.5 mr-3 text-primary-600 flex-shrink-0" style={{color: siteConfig.primary_color}} />
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900">
                      <EditableContent
                        page="policy"
                        section="attendance"
                        contentKey="rule_2_title"
                        defaultValue="Αγώνες"
                        tag="span"
                      />
                    </h4>
                    <p className="text-gray-700">
                      <EditableContent
                        page="policy"
                        section="attendance"
                        contentKey="rule_2_content"
                        defaultValue="Όλες οι παίκτριες πρέπει να βρίσκονται στο γήπεδο τουλάχιστον 90 λεπτά πριν την έναρξη κάθε αγώνα, εκτός αν δοθούν διαφορετικές οδηγίες από το τεχνικό επιτελείο."
                        tag="span"
                      />
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <SafeIcon icon={FiCheckCircle} className="w-5 h-5 mt-0.5 mr-3 text-primary-600 flex-shrink-0" style={{color: siteConfig.primary_color}} />
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900">
                      <EditableContent
                        page="policy"
                        section="attendance"
                        contentKey="rule_3_title"
                        defaultValue="Συνέπεια"
                        tag="span"
                      />
                    </h4>
                    <p className="text-gray-700">
                      <EditableContent
                        page="policy"
                        section="attendance"
                        contentKey="rule_3_content"
                        defaultValue="Η συνέπεια στις ώρες προπονήσεων και συγκεντρώσεων είναι απαραίτητη. Επαναλαμβανόμενες καθυστερήσεις θα αντιμετωπίζονται με κατάλληλες συνέπειες από το τεχνικό επιτελείο."
                        tag="span"
                      />
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
            
            {/* Sportsmanship */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="bg-gray-50 rounded-xl p-8 border-l-4 border-primary-600"
              style={{borderColor: siteConfig.primary_color}}
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <SafeIcon icon={FiAward} className="w-6 h-6 mr-3 text-primary-600" style={{color: siteConfig.primary_color}} />
                <EditableContent
                  page="policy"
                  section="detailed"
                  contentKey="sportsmanship_title"
                  defaultValue="Αθλητικό Πνεύμα"
                  tag="span"
                />
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-start">
                  <SafeIcon icon={FiCheckCircle} className="w-5 h-5 mt-0.5 mr-3 text-primary-600 flex-shrink-0" style={{color: siteConfig.primary_color}} />
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900">
                      <EditableContent
                        page="policy"
                        section="sportsmanship"
                        contentKey="rule_1_title"
                        defaultValue="Ευ Αγωνίζεσθαι"
                        tag="span"
                      />
                    </h4>
                    <p className="text-gray-700">
                      <EditableContent
                        page="policy"
                        section="sportsmanship"
                        contentKey="rule_1_content"
                        defaultValue="Όλα τα μέλη της ομάδας πρέπει να επιδεικνύουν αθλητικό πνεύμα και να σέβονται τους κανόνες του παιχνιδιού. Η νίκη είναι σημαντική, αλλά όχι εις βάρος της αθλητικής συμπεριφοράς."
                        tag="span"
                      />
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <SafeIcon icon={FiCheckCircle} className="w-5 h-5 mt-0.5 mr-3 text-primary-600 flex-shrink-0" style={{color: siteConfig.primary_color}} />
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900">
                      <EditableContent
                        page="policy"
                        section="sportsmanship"
                        contentKey="rule_2_title"
                        defaultValue="Αποφάσεις Διαιτητών"
                        tag="span"
                      />
                    </h4>
                    <p className="text-gray-700">
                      <EditableContent
                        page="policy"
                        section="sportsmanship"
                        contentKey="rule_2_content"
                        defaultValue="Οι αποφάσεις των διαιτητών πρέπει να γίνονται σεβαστές, ακόμη και αν διαφωνούμε με αυτές. Μόνο οι αρχηγοί και οι προπονητές μπορούν να συζητήσουν με τους διαιτητές, πάντα με κόσμιο τρόπο."
                        tag="span"
                      />
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <SafeIcon icon={FiCheckCircle} className="w-5 h-5 mt-0.5 mr-3 text-primary-600 flex-shrink-0" style={{color: siteConfig.primary_color}} />
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900">
                      <EditableContent
                        page="policy"
                        section="sportsmanship"
                        contentKey="rule_3_title"
                        defaultValue="Αντίπαλοι"
                        tag="span"
                      />
                    </h4>
                    <p className="text-gray-700">
                      <EditableContent
                        page="policy"
                        section="sportsmanship"
                        contentKey="rule_3_content"
                        defaultValue="Οι αντίπαλοι πρέπει να αντιμετωπίζονται με σεβασμό. Πρέπει να χαιρετάμε τους αντιπάλους πριν και μετά τον αγώνα, ανεξάρτητα από το αποτέλεσμα."
                        tag="span"
                      />
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Conclusion Section */}
      <section className="py-16 bg-primary-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              <EditableContent
                page="policy"
                section="conclusion"
                contentKey="title"
                defaultValue="Δέσμευση στην Πολιτική της Ομάδας"
                tag="span"
              />
            </h2>
            <div className="text-lg text-gray-700 prose max-w-none">
              <EditableContent
                page="policy"
                section="conclusion"
                contentKey="content"
                defaultValue="Όλα τα μέλη της ομάδας μας αναμένεται να τηρούν αυτούς τους κανονισμούς. Η συμμετοχή στον Α.Ο. Χανιά σημαίνει ότι αποδέχεστε και δεσμεύεστε να ακολουθείτε αυτές τις πολιτικές. Το τεχνικό επιτελείο διατηρεί το δικαίωμα να λάβει τα κατάλληλα μέτρα σε περίπτωση παραβίασης των κανονισμών, με στόχο πάντα τη διατήρηση ενός υγιούς και θετικού περιβάλλοντος για όλους."
                multiline={true}
                tag="p"
              />
            </div>
            
            <div className="mt-8">
              <a 
                href="/#/contact" 
                className="inline-flex items-center bg-primary-600 hover:bg-primary-700 text-white px-8 py-3 rounded-lg font-semibold transition-colors duration-200"
                style={{backgroundColor: siteConfig.primary_color}}
              >
                <EditableContent
                  page="policy"
                  section="conclusion"
                  contentKey="button"
                  defaultValue="Επικοινωνήστε μαζί μας"
                  tag="span"
                />
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default TeamPolicyPage;